/**
 * Calendario de consultorías propio (41, Robert 23-24/10/2026). Lógica pura de la app: agrupar los
 * huecos que da la base por día (en Madrid) y el horario de consultorías de cada persona. Quién está
 * libre y quién la hace lo decide la base (nevora_huecos, nevora_agendar_consultoria).
 */

const ZONA = "Europe/Madrid";

/** «aaaa-mm-dd» de un instante, en Madrid. */
export const diaMadrid = (iso: string): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: ZONA,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));

/** «HH:MM» de un instante, en Madrid. */
export const horaMadrid = (iso: string): string =>
  new Intl.DateTimeFormat("es-ES", {
    timeZone: ZONA,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));

export type DiaConHuecos = {
  dia: string;
  horas: { iso: string; hora: string }[];
};

/** Los huecos (instantes ISO) agrupados por día de Madrid, en orden. */
export const agruparHuecos = (huecos: string[]): DiaConHuecos[] => {
  const dias = new Map<string, DiaConHuecos>();
  for (const iso of [...huecos].sort()) {
    const dia = diaMadrid(iso);
    const d = dias.get(dia) ?? { dia, horas: [] };
    d.horas.push({ iso, hora: horaMadrid(iso) });
    dias.set(dia, d);
  }
  return [...dias.values()];
};

/** Una franja del horario de consultorías: día 1 (lunes) a 7 (domingo) y horas «HH:MM». */
export type Franja = { dia: number; desde: string; hasta: string };

const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Franjas válidas: día 1-7, horas «HH:MM» y la de inicio antes que la de fin (como la base). */
export const horarioValido = (franjas: Franja[]): boolean =>
  franjas.length <= 50 &&
  franjas.every(
    (f) =>
      Number.isInteger(f.dia) &&
      f.dia >= 1 &&
      f.dia <= 7 &&
      HORA.test(f.desde) &&
      HORA.test(f.hasta) &&
      f.desde < f.hasta,
  );

/**
 * Copia las franjas de un día a otros (Robert, 27/10/2026: «copiar y aplicar a los demás días», para no ir uno
 * por uno). Cada día de destino se queda exactamente con las franjas del de origen; los demás, igual.
 */
export const copiarDia = (
  franjas: Franja[],
  origen: number,
  destinos: number[],
): Franja[] => {
  const otros = destinos.filter((d) => d !== origen);
  const delOrigen = franjas.filter((f) => f.dia === origen);
  return [
    ...franjas.filter((f) => !otros.includes(f.dia)),
    ...otros.flatMap((dia) => delOrigen.map((f) => ({ ...f, dia }))),
  ].sort((a, b) => a.dia - b.dia || a.desde.localeCompare(b.desde));
};

/** Las franjas de la base (jsonb) en orden de día y hora; lo que no se entiende se ignora. */
export const leerHorario = (valor: unknown): Franja[] =>
  (Array.isArray(valor) ? valor : [])
    .map((f) => ({
      dia: Number((f as Franja)?.dia),
      desde: String((f as Franja)?.desde ?? ""),
      hasta: String((f as Franja)?.hasta ?? ""),
    }))
    .filter((f) => horarioValido([f]))
    .sort((a, b) => a.dia - b.dia || a.desde.localeCompare(b.desde));
